'use client';

import { useMemo } from 'react';
import { useGuidedTour } from './GuidedTourProvider';
import { useTourSpotlight } from './hooks/useTourSpotlight';
import { TourSpotlightProvider } from './TourSpotlightContext';
import { cn } from '@/lib/utils';

interface GuidedTourOverlayProps {
  children?: React.ReactNode;
}

export function GuidedTourOverlay({ children }: GuidedTourOverlayProps) {
  const { isOpen, engine, currentStep } = useGuidedTour();
  const { spotlightRect, focusTargetState } = useTourSpotlight({
    focusTarget: currentStep?.focus_target,
    revealTarget: currentStep?.reveal_target,
    spotlightReveal: currentStep?.spotlight_reveal ?? false,
    hasSelectedFlow: engine.hasSelectedFlow,
    isOpen,
    isMinimized: engine.isMinimized,
    isObjectiveIndexMode: engine.isObjectiveIndexMode,
  });

  const spotlightValue = useMemo(
    () => ({ spotlightRect, focusTargetState }),
    [spotlightRect, focusTargetState],
  );

  if (!isOpen) {
    return null;
  }

  const showSpotlight =
    !engine.isMinimized &&
    engine.viewMode === 'runner' &&
    spotlightRect &&
    spotlightRect.width > 0 &&
    spotlightRect.height > 0;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[300]"
      aria-hidden={!showSpotlight}
    >
      {showSpotlight && (
        <>
          <div
            className="pointer-events-none absolute rounded-lg shadow-[0_0_0_9999px_rgba(15,23,42,0.55)] ring-2 ring-white/60 transition-all duration-300 dark:shadow-[0_0_0_9999px_rgba(0,0,0,0.7)] dark:ring-slate-300/60"
            style={{
              left: spotlightRect.left - 6,
              top: spotlightRect.top - 6,
              width: spotlightRect.width + 12,
              height: spotlightRect.height + 12,
            }}
          />
          {focusTargetState === 'missing' && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-slate-900/80 px-3 py-1.5 text-xs text-white shadow-lg backdrop-blur-sm">
              Target not visible
            </div>
          )}
        </>
      )}
      <TourSpotlightProvider value={spotlightValue}>{children}</TourSpotlightProvider>
    </div>
  );
}
